/**
 * Lookahead scheduler: the "two clocks" pattern. A coarse JavaScript timer
 * wakes every 25 ms and hands every event that falls inside the next 120 ms to
 * the audio clock, which fires it sample-accurately. Injected clock and ticker
 * keep it testable without a browser.
 */

export interface Clock {
  /** Audio time in seconds. */
  now(): number;
}

export interface Ticker {
  start(callback: () => void): void;
  stop(): void;
}

export interface ScheduledEvent {
  /** Absolute audio time in seconds. */
  time: number;
  fire(time: number): void;
}

export class IntervalTicker implements Ticker {
  private handle: ReturnType<typeof setInterval> | null = null;
  constructor(private readonly intervalMs = 25) {}
  start(callback: () => void): void {
    this.stop();
    this.handle = setInterval(callback, this.intervalMs);
  }
  stop(): void {
    if (this.handle !== null) clearInterval(this.handle);
    this.handle = null;
  }
}

export interface SchedulerOptions {
  lookahead?: number;
  ticker?: Ticker;
  onDone?: () => void;
}

export class Scheduler {
  private queue: ScheduledEvent[] = [];
  private running = false;
  private endTime = 0;
  private readonly lookahead: number;
  private readonly ticker: Ticker;
  private readonly onDone: (() => void) | undefined;

  constructor(
    private readonly clock: Clock,
    options: SchedulerOptions = {},
  ) {
    this.lookahead = options.lookahead ?? 0.12;
    this.ticker = options.ticker ?? new IntervalTicker();
    this.onDone = options.onDone;
  }

  /** Adds events (any order) and keeps the queue sorted. */
  add(events: readonly ScheduledEvent[], endTime?: number): void {
    this.queue.push(...events);
    this.queue.sort((a, b) => a.time - b.time);
    const last = this.queue[this.queue.length - 1];
    this.endTime = Math.max(this.endTime, endTime ?? last?.time ?? 0);
  }

  get pending(): number {
    return this.queue.length;
  }

  get isRunning(): boolean {
    return this.running;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.tick();
    this.ticker.start(() => this.tick());
  }

  stop(): void {
    this.running = false;
    this.ticker.stop();
    this.queue = [];
  }

  /** Fires everything due inside the lookahead window. Public for tests. */
  tick(): void {
    if (!this.running) return;
    const horizon = this.clock.now() + this.lookahead;
    while (this.queue.length > 0 && (this.queue[0] as ScheduledEvent).time < horizon) {
      const event = this.queue.shift() as ScheduledEvent;
      // An event already in the past still fires, at "now": a late start is
      // better than a dropped note.
      event.fire(Math.max(event.time, this.clock.now()));
    }
    if (this.queue.length === 0 && this.clock.now() >= this.endTime) {
      this.running = false;
      this.ticker.stop();
      this.onDone?.();
    }
  }
}
